import classNames from "classnames";
import React from "react";
import { Utils } from "../../common/utils";
import RemovableTraveller from "./removabletraveller";
import ContactV2PaginatedDropdown from "components/dropdowns/paginated/ContactV2PaginatedDropdown";

interface Props {
  artist: server.dto.ArtistSmall
  travellers: server.dto.ContactSmallV2[]
  error?: boolean
  onTravellersChanged: (travellers: server.dto.ContactSmallV2[]) => void
}

interface State {
  suggestedTravellers: server.dto.ContactSmallV2[]
}

const mapKeyValueToContact = (item: server.dto.ContactWithWorkRelation): server.dto.ContactSmallV2 => (item.contact);

export default class TravellersRow extends React.Component<Props, State> {
  state: State = {
    suggestedTravellers: []
  };

  async componentDidMount() {
    const relevantContacts = await this.getRelevantContacts();
    this.setState({ suggestedTravellers: relevantContacts.map(mapKeyValueToContact) });
  }

  async componentDidUpdate(prevProps: Props) {
    if (prevProps.artist?.artistId !== this.props.artist?.artistId) {
      if (!this.props.artist?.artistId) {
        this.setState({ suggestedTravellers: [] });
        this.props.onTravellersChanged([]);
      } else {
        const relevantContactsKeyValue = await this.getRelevantContacts();
        const defaultSelectedTravellers = relevantContactsKeyValue
          .filter(r => r.defaultSelect)
          .map(mapKeyValueToContact);
        this.props.onTravellersChanged(defaultSelectedTravellers);

        const allTravellers = relevantContactsKeyValue
          .map(mapKeyValueToContact);
        this.setState({ suggestedTravellers: allTravellers });
      }
    }
  }

  getRelevantContacts = async (): Promise<server.dto.ContactWithWorkRelation[]> => {
    if (!this.props.artist?.artistId) {
      return [];
    }

    const instruction: server.dto.ListContactByCompanyInstruction = {
      companyId: this.props.artist.artistId,
      page: 1,
      perPage: 1000,
      isActive: true
    };

    const contacts = await window.CoreApi.CompanyToContact.ListContactByCompany(instruction);
    return contacts.data.data;
  };

  onTravellerAdded = (traveller: server.dto.ContactSmallV2) => {
    const travellers = Utils.uniqBy([...this.props.travellers, traveller], (contact) => contact.contactId);
    this.props.onTravellersChanged(travellers);
  };

  onTravellerRemoved = (item: server.dto.ContactSmallV2) => {
    const newTravellers = this.props.travellers.filter(t => t.contactId !== item.contactId);
    this.props.onTravellersChanged(newTravellers);
  };

  getSuggestedTravellers = (): server.dto.ContactSmallV2[] => {
    const { suggestedTravellers } = this.state;
    const { travellers } = this.props;

    const result: server.dto.ContactSmallV2[] = [];
    for (const suggestion of suggestedTravellers) {
      // Only get travellers who aren't already added to the flight
      if (travellers.findIndex(t => t.contactId === suggestion.contactId) === -1) {
        result.push(suggestion);
      }
    }

    return result.sort((a, b) => a.name.fullName.localeCompare(b.name.fullName));
  };

  public render(): JSX.Element {
    if (this.props.artist == null) {
      return null;
    }

    if (this.props.travellers == null) {
      return null;
    }

    const travellerSuggestionClass = classNames({
      hidden: this.state.suggestedTravellers.length === 0,
      padding: true
    });

    const travellersContainerClass = classNames({
      borderright: this.state.suggestedTravellers.length === 0,
      disable_select: true
    });

    const travellersClass = classNames({
      hidden: this.props.travellers.length === 0,
      itemchooserTable: true,
      padding: true
    });

    const sortedTravellers = this.props.travellers.sort((a, b) => a.name.fullName.localeCompare(b.name.fullName));
    const sortedSuggestedTravellers = this.getSuggestedTravellers();

    const anySuggestedTravellers = sortedSuggestedTravellers.length > 0;

    return <tr style={{ display: "table-row" }}>
      <td className="label">{RESX.Flight.Travellers}</td>
      <td style={{ minWidth: 350 }} className={travellersContainerClass}>
        <div className={travellersClass}>
          {sortedTravellers
            .map(traveller => (
              <RemovableTraveller key={traveller.contactId} traveller={traveller} onTravellerRemove={this.onTravellerRemoved} />
            ))
          }
        </div>
        <div>
          <ContactV2PaginatedDropdown
            key={this.state.suggestedTravellers.concat(this.props.travellers).map(t => t.contactId).join(";")}
            excludeKeys={this.state.suggestedTravellers.concat(this.props.travellers).map(t => t.contactId)}
            value={null}
            onChange={this.onTravellerAdded}
            config={{
              isSearchable: true,
              hasError: this.props.error
            }}
          />
        </div>

      </td>
      {anySuggestedTravellers && <td className={travellerSuggestionClass}>
        <b>{RESX.Planning.ConsiderIncluding}: </b>
        {sortedSuggestedTravellers.map((traveller, idx) => (
          <React.Fragment key={traveller.contactId}>
            <a
              style={{ whiteSpace: "nowrap" }}
              onClick={() => { this.onTravellerAdded(traveller); }}>
              {traveller.name.fullName}
            </a>{idx + 1 !== sortedSuggestedTravellers.length && ", "}
          </React.Fragment>
        ))}
      </td>}
    </tr>;
  }
}
